import type { TableDataRow } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { readThrough, type ReadResult } from '@/lib/offlineCache';
import { formatDate, formatNumber } from '@/lib/format';
import { downloadBytes } from '@/lib/printForm';
import { fileBase64 } from '@/lib/fileBytes';

export const SED_APP_CODE = 'sed';
export const SED_CONTRACTS_TABLE = 'sed_contracts';
export const SED_LETTERS_TABLE = 'sed_letters';
export const SED_ROUTE_CONTRACTS = 'sed_contract_approve';

export type SedDocumentKind = 'contract' | 'letter';
export type SedQuickFilter = 'all' | 'mine' | 'approving' | 'signed' | 'overdue';

export interface SedRouteSummary {
  id: string;
  routeCode: string;
  status: string;
  currentStep: number;
  stepsTotal: number;
  startedAt: string;
}

export interface SedContract {
  kind: 'contract';
  id: string;
  number: string;
  date: string;
  counterpartyId: string;
  counterpartyName: string;
  subject: string;
  amount: string;
  dateFrom: string;
  dateTo: string;
  autoProlong: boolean;
  status: string;
  statusLabel: string;
  responsibleId: string;
  responsibleName: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  route: SedRouteSummary | null;
}

export interface SedLetter {
  kind: 'letter';
  id: string;
  number: string;
  date: string;
  docTypeName: string;
  direction: string;
  directionLabel: string;
  counterpartyId: string;
  counterpartyName: string;
  subject: string;
  descr: string;
  executorId: string;
  executorName: string;
  dueDate: string;
  nomenclatureName: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  route: SedRouteSummary | null;
}

export type SedDocument = SedContract | SedLetter;

export interface SedSnapshot {
  contracts: SedContract[];
  letters: SedLetter[];
}

export interface SedFilterCounts {
  all: number;
  mine: number;
  approving: number;
  signed: number;
  overdue: number;
}

export interface SedStatusView {
  code: string;
  label: string;
  fromRoute: boolean;
}

export interface SedRouteStepInfo {
  order: number;
  name: string;
  kind: string;
  assigneeType: string;
  assigneeValue: string;
  status: string;
  comment: string;
  actedAt: string;
}

export interface SedRouteInstanceInfo {
  id: string;
  routeCode: string;
  status: string;
  currentStep: number;
  stepsTotal: number;
  startedAt: string;
  finishedAt: string;
  steps: SedRouteStepInfo[];
}

export interface SedRouteTemplateInfo {
  code: string;
  synonym: string;
  steps: { name: string; kind: string }[];
}

export interface SedRouteDetails {
  instance: SedRouteInstanceInfo | null;
  templates: SedRouteTemplateInfo[];
}

export interface SedFileInfo {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadedBy: string;
  uploadedAt: string;
}

const CONTRACT_STATUS_LABELS: Record<string, string> = {
  draft: 'Проект',
  approving: 'На согласовании',
  signed: 'Подписан',
  done: 'Завершён',
  terminated: 'Расторгнут',
};

const DIRECTION_LABELS: Record<string, string> = {
  in: 'Входящий',
  out: 'Исходящий',
  internal: 'Внутренний',
};

const ROUTE_STATUS_LABELS: Record<string, string> = {
  running: 'В процессе',
  approved: 'Согласовано',
  rejected: 'Отклонено',
  canceled: 'Отменено',
};

const ROUTE_STEP_LABELS: Record<string, string> = {
  pending: 'ожидает',
  approve: 'согласовано',
  reject: 'отклонено',
  skipped: 'пропущено',
  sign: 'подпись',
};

const PAGE_SIZE = 500;

export function sedWorkspaceCacheKey(): string {
  const base = useCenterStore.getState().baseSlug || 'local';
  return `sed-workspace:${base}`;
}

export function cellsOf(row: TableDataRow): Map<string, string> {
  const cells = new Map<string, string>();
  for (const value of row.values || []) {
    const raw = String(value.value ?? '');
    if (raw === '<nil>') continue;
    cells.set(String(value.column), raw);
  }
  return cells;
}

function text(cells: Map<string, string>, column: string): string {
  return String(cells.get(column) || '').trim();
}

function isTrue(raw: string): boolean {
  const value = String(raw || '').trim().toLowerCase();
  return value === 'true' || value === '1' || value === 'да';
}

function dateOnly(value: string): string {
  const raw = String(value || '').trim();
  if (!raw || raw === '<nil>') return '';
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

function todayISO(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function formatSedDate(value: string): string {
  return formatDate(value, 'dd.MM.yyyy');
}

export function formatSedDateTime(value: string): string {
  return formatDate(value, 'dd.MM.yyyy HH:mm');
}

export function formatSedAmount(value: string): string {
  const raw = String(value ?? '').trim();
  if (!raw || raw === '<nil>') return '';
  const fraction = raw.includes('.') ? raw.split('.')[1].replace(/0+$/, '').length : 0;
  const digits = Math.min(2, fraction);
  return formatNumber(raw, `${digits};group`);
}

export function formatSedFileSize(size: number): string {
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} Б`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} КБ`;
  return `${(size / (1024 * 1024)).toFixed(1)} МБ`;
}

export function sedRouteStatusLabel(status: string): string {
  return ROUTE_STATUS_LABELS[status] || status || '—';
}

export function sedRouteStepLabel(status: string): string {
  return ROUTE_STEP_LABELS[status] || status || '—';
}

export function contractStatusView(contract: SedContract): SedStatusView {
  if (contract.route?.status === 'running') {
    return { code: 'approving', label: 'На согласовании', fromRoute: true };
  }
  const code = contract.status;
  const label = contract.statusLabel || CONTRACT_STATUS_LABELS[code] || code || 'Без статуса';
  return { code, label, fromRoute: false };
}

export function contractOverdue(contract: SedContract): boolean {
  const due = dateOnly(contract.dateTo);
  if (!due) return false;
  if (contract.status === 'done' || contract.status === 'terminated') return false;
  return due < todayISO();
}

export function contractDaysLeft(contract: SedContract): number | null {
  const due = dateOnly(contract.dateTo);
  if (!due) return null;
  const end = new Date(`${due}T00:00:00`);
  const start = new Date(`${todayISO()}T00:00:00`);
  return Math.round((end.getTime() - start.getTime()) / 86400000);
}

export function letterStatusView(letter: SedLetter): SedStatusView {
  if (letter.route?.status === 'running') {
    return { code: 'approving', label: 'На согласовании', fromRoute: true };
  }
  if (letter.route?.status === 'approved') {
    return { code: 'signed', label: 'Согласовано', fromRoute: true };
  }
  const due = dateOnly(letter.dueDate);
  if (due) {
    const today = todayISO();
    if (due < today) return { code: 'overdue', label: 'Просрочено', fromRoute: false };
    if (due === today) return { code: 'today', label: 'Срок сегодня', fromRoute: false };
    const left = Math.round((new Date(`${due}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()) / 86400000);
    if (left > 0 && left <= 3) return { code: 'soon', label: 'Срок скоро', fromRoute: false };
    return { code: 'planned', label: 'В работе', fromRoute: false };
  }
  return { code: 'registered', label: 'Зарегистрировано', fromRoute: false };
}

export function letterOverdue(letter: SedLetter): boolean {
  const due = dateOnly(letter.dueDate);
  if (!due) return false;
  if (letter.route?.status === 'approved') return false;
  return due < todayISO();
}

export function sedDocumentStatusView(doc: SedDocument): SedStatusView {
  return doc.kind === 'contract' ? contractStatusView(doc) : letterStatusView(doc);
}

export function sedDocumentTitle(doc: SedDocument): string {
  const suffix = doc.number ? ` № ${doc.number}` : '';
  return `${doc.kind === 'contract' ? 'Договор' : 'Письмо'}${suffix}`;
}

export function sedDocumentSubtitle(doc: SedDocument): string {
  if (doc.kind === 'contract') return doc.subject || doc.counterpartyName;
  return doc.subject || doc.descr;
}

export function sedDocumentDueDate(doc: SedDocument): string {
  return doc.kind === 'contract' ? doc.dateTo : doc.dueDate;
}

export function sedDocumentMy(doc: SedDocument, userId: string): boolean {
  if (!userId) return false;
  if (doc.createdBy === userId) return true;
  if (doc.kind === 'contract') return doc.responsibleId === userId;
  return doc.executorId === userId;
}

export function sedDocumentApproving(doc: SedDocument): boolean {
  if (doc.route?.status === 'running') return true;
  return doc.kind === 'contract' && doc.status === 'approving';
}

export function sedDocumentSigned(doc: SedDocument): boolean {
  if (doc.kind === 'contract') return doc.status === 'signed';
  return doc.route?.status === 'approved';
}

export function sedDocumentOverdue(doc: SedDocument): boolean {
  return doc.kind === 'contract' ? contractOverdue(doc) : letterOverdue(doc);
}

export function filterSedDocuments<T extends SedDocument>(
  docs: T[],
  filter: SedQuickFilter,
  userId: string,
): T[] {
  if (filter === 'all') return docs;
  return docs.filter((doc) => {
    switch (filter) {
      case 'mine':
        return sedDocumentMy(doc, userId);
      case 'approving':
        return sedDocumentApproving(doc);
      case 'signed':
        return sedDocumentSigned(doc);
      case 'overdue':
        return sedDocumentOverdue(doc);
      default:
        return true;
    }
  });
}

export function countSedFilters(docs: SedDocument[], userId: string): SedFilterCounts {
  return {
    all: docs.length,
    mine: docs.filter((doc) => sedDocumentMy(doc, userId)).length,
    approving: docs.filter((doc) => sedDocumentApproving(doc)).length,
    signed: docs.filter((doc) => sedDocumentSigned(doc)).length,
    overdue: docs.filter((doc) => sedDocumentOverdue(doc)).length,
  };
}

export function searchSedDocuments<T extends SedDocument>(docs: T[], query: string): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return docs;
  return docs.filter((doc) => {
    const haystack = [
      doc.number,
      doc.subject,
      doc.counterpartyName,
      doc.createdByName,
      doc.kind === 'contract' ? doc.responsibleName : doc.executorName,
      doc.kind === 'letter' ? doc.directionLabel : doc.statusLabel,
      doc.kind === 'letter' ? doc.docTypeName : '',
    ].join(' ').toLowerCase();
    return haystack.includes(needle);
  });
}

async function listRows(
  table: string,
  options: { orderBy?: string; orderDesc?: boolean; filters?: Array<{ column: string; value: string }> } = {},
): Promise<TableDataRow[]> {
  const rows: TableDataRow[] = [];
  for (let page = 0; page < 20; page++) {
    const request: Record<string, unknown> = {
      schema: 'public',
      table,
      pageSize: PAGE_SIZE,
      page,
      includeMarked: false,
      filters: options.filters || [],
    };
    if (options.orderBy) {
      request.orderBy = options.orderBy;
      request.orderDesc = !!options.orderDesc;
    }
    const response: any = await getSDK().metadata.listTableData(request as any);
    const chunk = (response.rows || []) as TableDataRow[];
    rows.push(...chunk);
    const total = Number(response.totalCount || 0);
    if (chunk.length < PAGE_SIZE || rows.length >= total) break;
  }
  return rows;
}

function routeFromRow(row: TableDataRow): SedRouteSummary | null {
  const cells = cellsOf(row);
  const id = text(cells, 'id');
  if (!id) return null;
  return {
    id,
    routeCode: text(cells, 'route_code'),
    status: text(cells, 'status'),
    currentStep: Number(text(cells, 'current_step') || 0),
    stepsTotal: Number(text(cells, 'steps_total') || 0),
    startedAt: text(cells, 'started_at'),
  };
}

function routesByEntity(rows: TableDataRow[]): Map<string, SedRouteSummary> {
  const result = new Map<string, SedRouteSummary>();
  for (const row of rows) {
    const route = routeFromRow(row);
    if (!route) continue;
    const entityId = text(cellsOf(row), 'entity_id');
    if (!entityId) continue;
    const current = result.get(entityId);
    if (!current) {
      result.set(entityId, route);
      continue;
    }
    if (current.status !== 'running' && route.status === 'running') {
      result.set(entityId, route);
    }
  }
  return result;
}

function contractFromRow(row: TableDataRow, route: SedRouteSummary | null): SedContract {
  const cells = cellsOf(row);
  const status = text(cells, 'status');
  return {
    kind: 'contract',
    id: text(cells, 'id'),
    number: text(cells, 'number'),
    date: text(cells, 'date'),
    counterpartyId: text(cells, 'counterparty_id'),
    counterpartyName: text(cells, 'counterparty_id_presentation'),
    subject: text(cells, 'subject'),
    amount: text(cells, 'amount'),
    dateFrom: text(cells, 'date_from'),
    dateTo: text(cells, 'date_to'),
    autoProlong: isTrue(text(cells, 'auto_prolong')),
    status,
    statusLabel: text(cells, 'status_presentation') || CONTRACT_STATUS_LABELS[status] || status,
    responsibleId: text(cells, 'responsible_id'),
    responsibleName: text(cells, 'responsible_id_presentation'),
    createdBy: text(cells, 'created_by'),
    createdByName: text(cells, 'created_by_presentation'),
    createdAt: text(cells, 'created_at'),
    route,
  };
}

function letterFromRow(row: TableDataRow, route: SedRouteSummary | null): SedLetter {
  const cells = cellsOf(row);
  const direction = text(cells, 'direction');
  return {
    kind: 'letter',
    id: text(cells, 'id'),
    number: text(cells, 'number'),
    date: text(cells, 'date'),
    docTypeName: text(cells, 'doc_type_id_presentation'),
    direction,
    directionLabel: text(cells, 'direction_presentation') || DIRECTION_LABELS[direction] || direction,
    counterpartyId: text(cells, 'counterparty_id'),
    counterpartyName: text(cells, 'counterparty_id_presentation'),
    subject: text(cells, 'subject'),
    descr: text(cells, 'descr'),
    executorId: text(cells, 'executor_id'),
    executorName: text(cells, 'executor_id_presentation'),
    dueDate: text(cells, 'due_date'),
    nomenclatureName: text(cells, 'nomenclature_id_presentation'),
    createdBy: text(cells, 'created_by'),
    createdByName: text(cells, 'created_by_presentation'),
    createdAt: text(cells, 'created_at'),
    route,
  };
}

async function fetchSedSnapshot(): Promise<SedSnapshot> {
  const [contractRows, letterRows, contractRouteRows, letterRouteRows] = await Promise.all([
    listRows(SED_CONTRACTS_TABLE, { orderBy: 'date_to' }),
    listRows(SED_LETTERS_TABLE, { orderBy: 'due_date' }),
    listRows('route_instances', {
      orderBy: 'started_at',
      orderDesc: true,
      filters: [{ column: 'entity_code', value: SED_CONTRACTS_TABLE }],
    }),
    listRows('route_instances', {
      orderBy: 'started_at',
      orderDesc: true,
      filters: [{ column: 'entity_code', value: SED_LETTERS_TABLE }],
    }),
  ]);

  const contractRoutes = routesByEntity(contractRouteRows);
  const letterRoutes = routesByEntity(letterRouteRows);

  return {
    contracts: contractRows.map((row) => contractFromRow(row, contractRoutes.get(text(cellsOf(row), 'id')) || null)),
    letters: letterRows.map((row) => letterFromRow(row, letterRoutes.get(text(cellsOf(row), 'id')) || null)),
  };
}

export async function loadSedWorkspace(): Promise<ReadResult<SedSnapshot>> {
  return readThrough(sedWorkspaceCacheKey(), fetchSedSnapshot);
}

function mapRouteStep(step: any): SedRouteStepInfo {
  return {
    order: Number(step?.order || 0),
    name: String(step?.name || ''),
    kind: String(step?.kind || ''),
    assigneeType: String(step?.assigneeType || step?.assignee_type || ''),
    assigneeValue: String(step?.assigneeValue || step?.assignee_value || ''),
    status: String(step?.status || ''),
    comment: String(step?.comment || ''),
    actedAt: String(step?.actedAt || step?.acted_at || ''),
  };
}

function mapRouteInstance(instance: any): SedRouteInstanceInfo {
  return {
    id: String(instance?.id || ''),
    routeCode: String(instance?.routeCode || instance?.route_code || ''),
    status: String(instance?.status || ''),
    currentStep: Number(instance?.currentStep || instance?.current_step || 0),
    stepsTotal: Number(instance?.stepsTotal || instance?.steps_total || 0),
    startedAt: String(instance?.startedAt || instance?.started_at || ''),
    finishedAt: String(instance?.finishedAt || instance?.finished_at || ''),
    steps: ((instance?.steps || []) as any[]).map(mapRouteStep),
  };
}

function mapRouteTemplate(template: any): SedRouteTemplateInfo {
  return {
    code: String(template?.code || ''),
    synonym: String(template?.synonym || template?.name || template?.code || ''),
    steps: ((template?.steps || []) as any[]).map((step) => ({
      name: String(step?.name || ''),
      kind: String(step?.kind || ''),
    })),
  };
}

export async function loadSedRouteDetails(
  entityType: string,
  entityCode: string,
  entityId: string,
): Promise<SedRouteDetails> {
  const [instances, templates] = await Promise.all([
    getSDK().approval.listRouteInstances({ entityId }),
    getSDK().approval.listRouteTemplates({ entityType, entityCode }),
  ]);
  const list = ((instances?.items || []) as any[]).map(mapRouteInstance);
  const active = list.find((item) => item.status === 'running');
  return {
    instance: active || list[0] || null,
    templates: ((templates?.items || []) as any[]).map(mapRouteTemplate),
  };
}

export async function loadSedFiles(ownerId: string): Promise<SedFileInfo[]> {
  const response: any = await getSDK().files.listFiles({ ownerId, includeLinked: false });
  return ((response?.items || []) as any[]).map((file) => ({
    id: String(file.id || ''),
    fileName: String(file.fileName || file.file_name || ''),
    fileSize: Number(file.fileSize || file.file_size || 0),
    fileType: String(file.fileType || file.file_type || ''),
    uploadedBy: String(file.uploadedBy || file.uploaded_by || ''),
    uploadedAt: String(file.uploadedAt || file.uploaded_at || ''),
  }));
}

export async function downloadSedFile(file: SedFileInfo): Promise<void> {
  const response: any = await getSDK().files.downloadFile({ id: file.id });
  downloadBytes(
    file.fileName || 'document',
    file.fileType || 'application/octet-stream',
    fileBase64(response?.data),
  );
}
